import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearError } from "@/store/slices/authSlice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import api from "@/utils/api";

const LoginForm = () => {
  const [loginMethod, setLoginMethod] = useState("password");
  const [formData, setFormData] = useState({
    phone_or_email: "",
    password: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);

  const [phoneOtp, setPhoneOtp] = useState(Array(6).fill(""));
  const [showOtpBox, setShowOtpBox] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const phoneOrEmailRef = useRef();
  const passwordRef = useRef();
  const otpRefs = useRef([]);

  const inputRefs = {
    phone_or_email: phoneOrEmailRef,
    password: passwordRef,
  };

  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  useEffect(() => {
    setValidationErrors({});
    setLoginError(null);
    setShowOtpBox(false);
    setOtpSent(false);
    setPhoneOtp(Array(6).fill(""));
    setFormData({ ...formData, password: "" });
  }, [loginMethod]);

  const validateForm = () => {
    const errors = {};
    if (!formData.phone_or_email.trim()) {
      errors.phone_or_email = "Mobile Number or Email is required";
    }
    if (loginMethod === "password" && !formData.password) {
      errors.password = "Password is required";
    }
    return errors;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    dispatch(clearError());
    setLoginError(null);

    const errors = validateForm();
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      inputRefs[firstErrorField]?.current?.focus();
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/login", formData);

      localStorage.setItem("token", response.data.token);

      dispatch({
        type: "auth/login/fulfilled",
        payload: response.data,
      });

      toast({
        title: "",
        description: "Logged in successfully",
        variant: "success",
        duration: 3000
      });

      navigate("/dashboard");
    } catch (err) {
      console.error("Login failed:", err);

      const errorMessage = err.response?.data?.message || "Login failed";
      setLoginError(errorMessage);
      dispatch({
        type: "auth/login/rejected",
        payload: { message: errorMessage },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async () => {
    if (!formData.phone_or_email.trim()) {
      setValidationErrors({
        phone_or_email: "Mobile Number or Email is required",
      });
      return;
    }

    setLoading(true);
    setLoginError(null);

    try {
      const response = await api.post("/request-login-otp", {
        phone_or_email: formData.phone_or_email,
      });

      if (response.data.status === 200 || response.data.status === 201) {
        setOtpSent(true);
        setShowOtpBox(true);
        setPhoneOtp(Array(6).fill(""));
        setOtpTimer(60);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);

        toast({
          title: "Success",
          description: response.data.message || "OTP sent successfully",
          variant: "success",
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to send OTP";
      setLoginError(errorMessage);
      setShowOtpBox(false);
      setOtpSent(false);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpTimer > 0) return;

    setLoading(true);

    try {
      const response = await api.post("/resend-login-otp", {
        phone_or_email: formData.phone_or_email,
      });

      setPhoneOtp(Array(6).fill(""));
      setOtpTimer(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);

      toast({
        title: "Success",
        description: response.data.message || "OTP resent successfully",
        variant: "success",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.message || "Failed to resend OTP",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const otpArray = [...phoneOtp];
    otpArray[index] = value;
    setPhoneOtp(otpArray);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
    if (!value && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpLogin = async () => {
    const otpCode = phoneOtp.join("");

    if (otpCode.length !== 6) {
      toast({
        title: "Error",
        description: "Please enter complete 6-digit OTP",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setLoginError(null);

    try {
      const response = await api.post("/login-with-otp", {
        phone_or_email: formData.phone_or_email,
        otp: otpCode,
      });

      localStorage.setItem("token", response.data.token);

      dispatch({
        type: "auth/login/fulfilled",
        payload: response.data,
      });

      toast({
        title: "",
        description: "Logged in successfully",
        variant: "success",
      });

      navigate("/dashboard");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "OTP verification failed";
      setLoginError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBlur = (field) => {
    const errors = validateForm();
    setValidationErrors((prev) => ({ ...prev, [field]: errors[field] }));
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });

    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    if (loginError) {
      setLoginError(null);
      dispatch(clearError());
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
        <button
          type="button"
          onClick={() => setLoginMethod("password")}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
            loginMethod === "password"
              ? "bg-white text-primary shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Login with Password
        </button>
        <button
          type="button"
          onClick={() => setLoginMethod("otp")}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
            loginMethod === "otp"
              ? "bg-white text-primary shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Login with OTP
        </button>
      </div>

      {loginError && (
        <Alert variant="destructive">
          <AlertDescription>{loginError}</AlertDescription>
        </Alert>
      )}

      <form
        onSubmit={
          loginMethod === "password"
            ? handlePasswordLogin
            : (e) => e.preventDefault()
        }
        className="space-y-4"
        noValidate
      >
        <div className="space-y-2">
          <Label htmlFor="phone_or_email" required>
            {loginMethod == "password"
              ? "Mobile Number or Email"
              : "Mobile Number"}
          </Label>
          <Input
            id="phone_or_email"
            ref={phoneOrEmailRef}
            onPaste={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            onDrop={(e) => e.preventDefault()}
            onKeyDown={(e) => {
              if (
                (e.ctrlKey || e.metaKey) &&
                ["v", "c", "x"].includes(e.key.toLowerCase())
              ) {
                e.preventDefault();
              }
            }}
            type="text"
            value={formData.phone_or_email}
            onChange={(e) =>
              handleInputChange("phone_or_email", e.target.value)
            }
            onBlur={() => handleBlur("phone_or_email")}
            className={`w-full border px-3 py-2 ${
              validationErrors.phone_or_email
                ? "border-red-500"
                : "border-gray-300"
            }`}
            disabled={loading || (loginMethod === "otp" && otpSent)}
          />
          {validationErrors.phone_or_email && (
            <p className="text-red-500 text-sm mt-1">
              {validationErrors.phone_or_email}
            </p>
          )}
        </div>
        {loginMethod === "password" && (
          <div className="space-y-2 relative">
            <Label htmlFor="password" required>
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                ref={passwordRef}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
                onKeyDown={(e) => {
                  if (
                    (e.ctrlKey || e.metaKey) &&
                    ["v", "c", "x"].includes(e.key.toLowerCase())
                  ) {
                    e.preventDefault();
                  }
                }}
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                onBlur={() => handleBlur("password")}
                className={`w-full pr-10 border px-3 py-2 ${
                  validationErrors.password
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                disabled={loading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-500" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-500" />
                )}
              </button>
            </div>
            {validationErrors.password && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.password}
              </p>
            )}

            <div className="text-right text-sm mt-1">
              <Link to="/forgot-password" className="text-primary underline">
                Forgot Password?
              </Link>
            </div>
          </div>
        )}
        {loginMethod === "otp" && (
          <div className="space-y-3">
            {!showOtpBox ? (
              <Button
                type="button"
                className="w-full"
                onClick={handleRequestOtp}
                disabled={loading || !formData.phone_or_email.trim()}
              >
                {loading ? "Sending OTP..." : "Get OTP"}
              </Button>
            ) : (
              <div className="space-y-3">
                <Label>Enter 6-Digit OTP</Label>
                <div className="flex items-center gap-2">
                  {phoneOtp.map((digit, i) => (
                    <Input
                      key={i}
                      type="text"
                      maxLength={1}
                      value={digit}
                      ref={(el) => (otpRefs.current[i] = el)}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      className="w-12 h-12 text-center text-lg font-semibold"
                      disabled={loading}
                    />
                  ))}
                </div>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={otpTimer > 0 || loading}
                    className={`${
                      otpTimer > 0
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-primary underline"
                    }`}
                  >
                    {otpTimer > 0 ? `Resend OTP in ${otpTimer}s` : "Resend OTP"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowOtpBox(false);
                      setOtpSent(false);
                      setPhoneOtp(Array(6).fill(""));
                    }}
                    className="text-gray-600 underline"
                    disabled={loading}
                  >
                    Change Number
                  </button>
                </div>

                <Button
                  type="button"
                  className="w-full"
                  onClick={handleOtpLogin}
                  disabled={phoneOtp.join("").length !== 6 || loading}
                >
                  {loading ? "Verifying..." : "Verify & Login"}
                </Button>
              </div>
            )}
          </div>
        )}
        {loginMethod === "password" && (
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Logging in..." : "Login"}
          </Button>
        )}
      </form>

      <div className="text-center text-sm">
        Don't have an account?{" "}
        <Link to="/register" className="text-primary underline">
          Register
        </Link>
      </div>
    </div>
  );
};

export default LoginForm;
